import React, { createContext, useContext, useMemo } from 'react';
import TagsSelect from './TagsSelect';
import {
  GENERAL_PERMISSIONS,
  ROLES_DATA,
  USER_PERMISSIONS_ERRORS,
  USER_TYPES,
} from '../constants';
import { message, Tooltip } from 'antd';
import { UnorderedListOutlined } from '@ant-design/icons';
import PermissionsSwitch from './PermissionsSwitch';
import { capitalize } from 'lodash';

interface UserPermissionsContextType {
  handlePermissionChange: (args: {
    key: string;
    value: boolean;
    uuid: string;
    isCurrentUser: boolean;
    generalPermissions: Record<string, boolean>;
    rest: Record<string, unknown>;
    selectedValue: string | null;
  }) => void;
}

const UserPermissionsContext = createContext<UserPermissionsContextType | null>(
  null,
);

type UserLevelPermissionsProps = {
  handleAssignPermissions: (userPermissions: {
    userAuth0ID: string;
    generalPermissions: Record<string, boolean>;
    [key: string]: unknown;
  }) => void;
  children: React.ReactNode;
};

const UserLevelPermissions = ({
  handleAssignPermissions,
  children,
}: UserLevelPermissionsProps) => {
  const handlePermissionChange = useMemo(
    () =>
      ({
        key,
        value,
        uuid,
        isCurrentUser,
        generalPermissions,
        rest,
        selectedValue,
      }: {
        key: string;
        value: boolean;
        uuid: string;
        isCurrentUser: boolean;
        generalPermissions: Record<string, boolean>;
        rest: Record<string, unknown>;
        selectedValue: string | null;
      }) => {
        if (!selectedValue) {
          return message.error('Select a user type first');
        }

        if (
          key === GENERAL_PERMISSIONS.ADMIN &&
          selectedValue !== USER_TYPES.CORPORATE.toUpperCase()
        ) {
          return message.error(USER_PERMISSIONS_ERRORS.CORPORATE_PERMISSIONS);
        }

        if (
          generalPermissions[key] &&
          key === GENERAL_PERMISSIONS.ACTIVE &&
          isCurrentUser
        ) {
          return message.error(USER_PERMISSIONS_ERRORS.ACTIVE_PERMISSIONS);
        }

        if (
          generalPermissions[key] &&
          key === GENERAL_PERMISSIONS.ADMIN &&
          isCurrentUser
        ) {
          return message.error(USER_PERMISSIONS_ERRORS.ADMIN_PERMISSIONS);
        }

        handleAssignPermissions({
          ...rest,
          userAuth0ID: uuid,
          generalPermissions: {
            ...generalPermissions,
            [key]: value,
          },
        });
      },
    [handleAssignPermissions],
  );

  const contextValue = useMemo(
    () => ({ handlePermissionChange }),
    [handlePermissionChange],
  );

  return (
    <UserPermissionsContext.Provider value={contextValue}>
      {children}
    </UserPermissionsContext.Provider>
  );
};

type UserLevelPermissionsTagsSelectProps = {
  userId: string;
  userPermissions: {
    userType?: string;
  };
  displayIconList?: boolean;
  selectedRowKeys: React.Key[];
  handleSelectedValueChange: (value: string[], uuid: string) => void;
  handleOpenPermissionsModal: (uuid: string) => void;
};

UserLevelPermissions.TagsSelect = ({
  userId,
  userPermissions,
  displayIconList,
  selectedRowKeys,
  handleSelectedValueChange,
  handleOpenPermissionsModal,
}: UserLevelPermissionsTagsSelectProps) => {
  return (
    <section>
      <div className="w-full flex items-center gap-2.5 min-w-[180px]">
        <TagsSelect
          rolesData={ROLES_DATA}
          disabled={selectedRowKeys.length > 1}
          selectedValue={userPermissions?.userType || []}
          updateSelectValue={(value: string[]) =>
            handleSelectedValueChange(value, userId)
          }
        />
        {displayIconList ? (
          <Tooltip title="Edit list">
            <UnorderedListOutlined
              onClick={() => {
                if (selectedRowKeys.length > 1) return;
                handleOpenPermissionsModal(userId);
              }}
              style={{ fontSize: '16px', color: '#437EF7' }}
              className={`transition-transform duration-300 group-hover:scale-125`}
            />
          </Tooltip>
        ) : null}
      </div>
    </section>
  );
};

type UserLevelPermissionsTogglePermissionsProps = {
  permissionKey: string;
  value: boolean;
  uuid: string;
  disabled: boolean;
  isCurrentUser: boolean;
  generalPermissions: Record<string, boolean>;
  rest: Record<string, unknown>;
  selectedValue: string | null;
};

UserLevelPermissions.TogglePermissions = function TogglePermissions({
  permissionKey,
  value,
  uuid,
  disabled,
  isCurrentUser,
  generalPermissions,
  rest,
  selectedValue,
}: UserLevelPermissionsTogglePermissionsProps) {
  const { handlePermissionChange } = useContext(UserPermissionsContext)!;

  return (
    <PermissionsSwitch
      key={permissionKey}
      uuid={uuid}
      permissionKey={permissionKey}
      label={capitalize(permissionKey)}
      checked={value}
      disabled={disabled}
      onChange={(value) =>
        handlePermissionChange({
          key: permissionKey,
          value,
          uuid,
          isCurrentUser,
          generalPermissions,
          rest,
          selectedValue,
        })
      }
    />
  );
};

export default UserLevelPermissions;
